import type { OnInit } from '@angular/core';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import type { AbstractControl, FormControlStatus, ValidationErrors } from '@angular/forms';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
    applyColgroupWidths,
    convertFileToBase64,
    Indent,
    LineHeight,
    OrderedTextAlign,
    PageBreak,
    PlaceholderMention,
    RichImage,
    TableCellStyle,
    TableStyle,
    TabSpan,
    USER_INPUT_DEBOUNCE_TIME,
    validateNoEmptyField,
} from '@modeso/aumico-lib-core-fe';
import { SUPPORTED_PLACE_HOLDERS } from '@modeso/aumico-lib-core-fe/constants/constants';
import { FilePreviewStatus } from '@modeso/aumico-lib-cpmm-fe/enums/FilePreviewStatus.enum';
import type { IComplementaryReport, ITextSnippetDTO } from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type { AnyExtension } from '@tiptap/core';
import { Editor } from '@tiptap/core';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';
import { I18NextService } from 'angular-i18next';
import fastDeepEqual from 'fast-deep-equal/es6';
import { cloneDeep } from 'lodash-es';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@UntilDestroy()
@Component({
    selector: 'lib-complementary-reports-component',
    templateUrl: 'complementary-reports.component.html',
    styleUrls: ['complementary-reports.component.scss'],
})
export class ComplementaryReportsComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() complementaryReport: IComplementaryReport;
    @Input() updatedComplementaryReports: IComplementaryReport[] = [];
    @Input() reportIndex: number | undefined;
    @Input() isUploading: boolean | undefined;

    @Output() deleteReportClicked: EventEmitter<void> = new EventEmitter<void>();
    @Output() printComplementaryReport = new EventEmitter<{ index: number; fileId?: string }>();
    @Output() complementaryReportChanged: EventEmitter<IComplementaryReport> = new EventEmitter<IComplementaryReport>();
    @Output() formStatusChanged: EventEmitter<FormControlStatus> = new EventEmitter<FormControlStatus>();
    isPreviewModalOpen: boolean;
    @Input()
    set formStatus(value: FormControlStatus) {
        if (value === 'INVALID') {
            this.markTitleAsDirty();
        }
    }

    public formGroup: UntypedFormGroup | undefined;
    public tiptapEditor: Editor | null = null;
    public tiptapValue = '';
    @ViewChild('tiptapHost') tiptapHost?: ElementRef<HTMLDivElement>;
    @ViewChild('tiptapImageInput') tiptapImageInput?: ElementRef<HTMLInputElement>;
    public isParagraphMenuOpen = false;
    public isAlignMenuOpen = false;
    public isLineHeightMenuOpen = false;
    public isTableMenuOpen = false;
    public isImageSizeMenuOpen = false;
    public isCellAlignMenuOpen = false;
    public isCellVerticalMenuOpen = false;
    public showInToc = true;
    public showTitleInReport = true;
    public showPageNumbers = true;
    showTextSnippetsListDialog: any;
    textSnippetToInsert: ITextSnippetDTO;

    @Input()
    set reportTitle(value: string) {
        if (this.formGroup != null && value != null) {
            this.formGroup.get('title').setValue(value);
        }
    }

    @Input() previewUrl: string | undefined;
    @Input() previewStatus: FilePreviewStatus | undefined;
    @Output() previewRequested = new EventEmitter<string>();
    @Output() previewClosed = new EventEmitter<string>();

    @Output() downloadFile = new EventEmitter<{ documentName?: string, documentId: string }>();
    @Output() openFile = new EventEmitter<string>();

    constructor(private readonly localizedTexts: I18NextService) { }

    ngOnInit(): void {
        this.initializeForm();
        this.monitorFormChanges();

        if (this.complementaryReport != null && this.complementaryReport.showInTOC != null) {
            this.showInToc = this.complementaryReport.showInTOC;
        }
        if (this.complementaryReport != null && this.complementaryReport.showPageNumbers != null) {
            this.showPageNumbers = this.complementaryReport.showPageNumbers;
        }
        if (this.complementaryReport != null && this.complementaryReport.showTitleInReport != null) {
            this.showTitleInReport = this.complementaryReport.showTitleInReport;
        }
    }

    ngAfterViewInit(): void {
        this.ensureTiptapEditor();
    }

    ngOnDestroy(): void {
        this.tiptapEditor?.destroy();
        this.tiptapEditor = null;
    }

    private initializeForm(): void {
        this.formGroup = new UntypedFormGroup({
            title: new UntypedFormControl(null, [
                (control: AbstractControl): ValidationErrors | null => Validators.required(control),
                validateNoEmptyField(),
            ]),
            content: new UntypedFormControl(null),
            showInToc: new UntypedFormControl(true),
            showPageNumbers: new UntypedFormControl(true),
            showTitleInReport: new UntypedFormControl(true),
        });

        if (this.complementaryReport.title != null) {
            this.formGroup.controls['title'].setValue(this.complementaryReport.title);
            this.formGroup.controls['content'].setValue(this.complementaryReport.content);
        }
        if (this.complementaryReport.showInTOC != null) {
            this.formGroup.controls['showInToc'].setValue(this.complementaryReport.showInTOC);
        }
        if (this.complementaryReport.showPageNumbers != null) {
            this.formGroup.controls['showPageNumbers'].setValue(this.complementaryReport.showPageNumbers);
        }
        if (this.complementaryReport.showTitleInReport != null) {
            this.formGroup.controls['showTitleInReport'].setValue(this.complementaryReport.showTitleInReport);
        }

        this.syncTiptapContent();
    }

    private monitorFormChanges(): void {
        if (this.formGroup == null) {
            throw new Error('Form group is undefined.');
        }
        this.formGroup.valueChanges
            .pipe(
                debounceTime(USER_INPUT_DEBOUNCE_TIME),
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                untilDestroyed(this),
            )
            .subscribe(() => this.handleFormChanges());

        this.formGroup
            .get('showInToc')
            .valueChanges.pipe(
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                untilDestroyed(this),
            )
            .subscribe((value) => this.onShowInTocToggleChanged(value));

        this.formGroup
            .get('showPageNumbers')
            .valueChanges.pipe(
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                untilDestroyed(this),
            )
            .subscribe((value) => this.onShowPageNumbersToggleChanged(value));

        this.formGroup
            .get('showTitleInReport')
            .valueChanges.pipe(
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                untilDestroyed(this),
            )
            .subscribe((value) => this.onShowTitleInReportToggleChanged(value));

        this.formGroup.statusChanges
            .pipe(debounceTime(USER_INPUT_DEBOUNCE_TIME), distinctUntilChanged(), untilDestroyed(this))
            .subscribe((statusChange) => this.formStatusChanged.emit(statusChange));
    }

    private handleFormChanges(): void {
        const title = this.formGroup?.get('title')?.value as string | null;

        const titleTrimmed = title ?? null;

        const content = this.formGroup?.get('content')?.value;

        if (content != null && typeof content !== 'string') {
            throw new Error('Content is not a string or undefined');
        }

        const updatedComplementaryReport: IComplementaryReport = {
            title: titleTrimmed,
            content: content != null ? content.trim() : null,
            showInTOC: this.showInToc,
            showPageNumbers: this.showPageNumbers,
            showTitleInReport: this.showTitleInReport,
            printLocation: this.updatedComplementaryReports[this.reportIndex].printLocation,
            sortOrderId: this.updatedComplementaryReports[this.reportIndex].sortOrderId,
            fileId: this.updatedComplementaryReports[this.reportIndex]?.fileId,
            fileName: this.updatedComplementaryReports[this.reportIndex]?.fileName,
        };

        this.complementaryReport = cloneDeep<IComplementaryReport>(updatedComplementaryReport);

        this.complementaryReportChanged.emit(updatedComplementaryReport);
        this.updateTiptapFromForm();
    }


    private ensureTiptapEditor(): void {
        if (this.tiptapEditor || !this.tiptapHost?.nativeElement) {
            return;
        }

        const extensions: AnyExtension[] = [
            StarterKit as unknown as AnyExtension,
            Underline as unknown as AnyExtension,
            OrderedTextAlign.configure({ types: ['heading', 'paragraph'] }) as unknown as AnyExtension,
            LineHeight,
            Indent,
            RichImage.configure({ inline: false }) as unknown as AnyExtension,
            PlaceholderMention.configure({
                HTMLAttributes: { class: 'mention' },
                suggestion: this.createMentionSuggestion(),
            }) as unknown as AnyExtension,
            Table.configure({ resizable: true }) as unknown as AnyExtension,
            TableRow as unknown as AnyExtension,
            TableHeader as unknown as AnyExtension,
            TableCell as unknown as AnyExtension,
            PageBreak,
            TabSpan,
            TableStyle,
            TableCellStyle,
        ];

        this.tiptapEditor = new Editor({
            element: this.tiptapHost.nativeElement,
            extensions,
            content: this.tiptapValue,
            onUpdate: ({ editor }) => {
                this.handleTiptapUpdate(editor.getHTML());
            },
        });
    }

    private syncTiptapContent(): void {
        const content = this.formGroup?.get('content')?.value as string | null;
        const rawContent = content ?? '';
        const normalizedContent = this.normalizeRichTextHtml(rawContent);
        if (this.formGroup && normalizedContent !== rawContent) {
            this.formGroup.get('content')?.setValue(normalizedContent, { emitEvent: false });
        }
        this.tiptapValue = applyColgroupWidths(normalizedContent);
        this.ensureTiptapEditor();
        if (this.tiptapEditor) {
            this.tiptapEditor.commands.setContent(this.tiptapValue, false);
        }
    }

    private updateTiptapFromForm(): void {
        if (!this.tiptapEditor) {
            return;
        }

        const content = this.formGroup?.get('content')?.value as string | null;
        const rawContent = content ?? '';
        const normalizedContent = this.normalizeRichTextHtml(rawContent);
        if (this.formGroup && normalizedContent !== rawContent) {
            this.formGroup.get('content')?.setValue(normalizedContent, { emitEvent: false });
        }
        const nextValue = applyColgroupWidths(normalizedContent);
        if (nextValue !== this.tiptapValue) {
            this.tiptapValue = nextValue;
            this.tiptapEditor.commands.setContent(this.tiptapValue, false);
        }
    }

    private handleTiptapUpdate(html: string): void {
        const normalizedHtml = this.normalizeRichTextHtml(html);
        this.tiptapValue = normalizedHtml;
        const control = this.formGroup?.get('content');
        if (control) {
            control.setValue(normalizedHtml, { emitEvent: false });
        }
        this.handleFormChanges();
    }

    private normalizeRichTextHtml(html: string): string {
        if (!html) {
            return html;
        }
        let normalized = html.replace(/<p>(\s|&nbsp;)*<\/p>/gi, '<p>&nbsp;</p>');
        normalized = normalized.replace(/<p><br\s*\/?>\s*<\/p>/gi, '<p>&nbsp;</p>');
        return normalized;
    }

    tiptapUndo(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.undo?.();
    }

    tiptapRedo(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.redo?.();
    }

    tiptapToggleBold(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.toggleBold?.();
    }

    tiptapToggleItalic(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.toggleItalic?.();
    }

    tiptapToggleStrike(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.toggleStrike?.();
    }

    tiptapRemoveFormat(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.clearNodes?.();
        (this.tiptapEditor as any)?.commands?.unsetAllMarks?.();
    }

    tiptapToggleBlockquote(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.toggleBlockquote?.();
    }

    tiptapToggleHeading(level: 1 | 2 | 3 | 4 | 5 | 6 = 2): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.toggleHeading?.({ level });
    }

    tiptapToggleBulletList(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.toggleBulletList?.();
    }

    tiptapToggleOrderedList(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.toggleOrderedList?.();
    }

    tiptapInsertPageBreak(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.insertPageBreak?.();
    }

    tiptapInsertTabSpan(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.insertTabSpan?.();
    }

    tiptapToggleUnderline(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.toggleUnderline?.();
    }

    tiptapSetAlign(value: 'left' | 'center' | 'right' | 'justify'): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.setTextAlign?.(value);
    }

    tiptapSetLineHeight(value: number): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.setLineHeight?.(value);
    }

    tiptapUnsetLineHeight(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.unsetLineHeight?.();
    }

    tiptapIndent(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.indent?.();
    }

    tiptapOutdent(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.outdent?.();
    }

    tiptapSelectAll(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.selectAll?.();
    }

    tiptapInsertImage(): void {
        this.tiptapFocus();
        this.tiptapImageInput?.nativeElement?.click();
    }

    tiptapToggleImageSizeMenu(): void {
        this.isImageSizeMenuOpen = !this.isImageSizeMenuOpen;
        this.isParagraphMenuOpen = false;
        this.isAlignMenuOpen = false;
        this.isLineHeightMenuOpen = false;
        this.isTableMenuOpen = false;
        this.isCellAlignMenuOpen = false;
        this.isCellVerticalMenuOpen = false;
    }

    tiptapSetImageSize(size: 'auto' | '25' | '50' | '75' | '100'): void {
        if (!this.tiptapEditor || !(this.tiptapEditor as any).isActive?.('image')) {
            return;
        }

        const width = size === 'auto' ? null : `${size}%`;
        const style = size === 'auto' ? null : `width: ${size}%; height: auto;`;

        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.updateAttributes?.('image', {
            width,
            height: null,
            style,
        });
        this.isImageSizeMenuOpen = false;
    }

    async onTiptapImageSelected(file: File): Promise<void> {
        if (!file?.type?.startsWith('image/')) {
            return;
        }

        try {
            const base64 = await convertFileToBase64(file);
            if (!base64) {
                return;
            }
            this.tiptapFocus();
            (this.tiptapEditor as any)?.commands?.setImage?.({ src: base64 });
        } catch (error) {
            console.error('Failed to upload image', error);
        }
    }

    toggleParagraphMenu(): void {
        this.isParagraphMenuOpen = !this.isParagraphMenuOpen;
        this.isAlignMenuOpen = false;
        this.isLineHeightMenuOpen = false;
        this.isTableMenuOpen = false;
        this.isCellAlignMenuOpen = false;
        this.isCellVerticalMenuOpen = false;
    }

    toggleAlignMenu(): void {
        this.isAlignMenuOpen = !this.isAlignMenuOpen;
        this.isParagraphMenuOpen = false;
        this.isLineHeightMenuOpen = false;
        this.isTableMenuOpen = false;
        this.isCellAlignMenuOpen = false;
        this.isCellVerticalMenuOpen = false;
    }

    toggleLineHeightMenu(): void {
        this.isLineHeightMenuOpen = !this.isLineHeightMenuOpen;
        this.isParagraphMenuOpen = false;
        this.isAlignMenuOpen = false;
        this.isTableMenuOpen = false;
        this.isCellAlignMenuOpen = false;
        this.isCellVerticalMenuOpen = false;
    }

    toggleTableMenu(): void {
        this.isTableMenuOpen = !this.isTableMenuOpen;
        this.isParagraphMenuOpen = false;
        this.isAlignMenuOpen = false;
        this.isLineHeightMenuOpen = false;
        this.isImageSizeMenuOpen = false;
        this.isCellAlignMenuOpen = false;
        this.isCellVerticalMenuOpen = false;
    }

    tiptapToggleCellAlignMenu(): void {
        this.isCellAlignMenuOpen = !this.isCellAlignMenuOpen;
        this.isCellVerticalMenuOpen = false;
        this.isParagraphMenuOpen = false;
        this.isAlignMenuOpen = false;
        this.isLineHeightMenuOpen = false;
        this.isTableMenuOpen = false;
        this.isImageSizeMenuOpen = false;
    }

    tiptapToggleCellVerticalMenu(): void {
        this.isCellVerticalMenuOpen = !this.isCellVerticalMenuOpen;
        this.isCellAlignMenuOpen = false;
        this.isParagraphMenuOpen = false;
        this.isAlignMenuOpen = false;
        this.isLineHeightMenuOpen = false;
        this.isTableMenuOpen = false;
        this.isImageSizeMenuOpen = false;
    }

    tiptapSetCellAlign(value: 'left' | 'center' | 'right' | null): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.setTableCellTextAlign?.(value);
        this.isCellAlignMenuOpen = false;
    }

    tiptapSetCellVerticalAlign(value: 'top' | 'middle' | 'bottom' | null): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.setTableCellVerticalAlign?.(value);
        this.isCellVerticalMenuOpen = false;
    }

    closeMenus(): void {
        this.isParagraphMenuOpen = false;
        this.isAlignMenuOpen = false;
        this.isLineHeightMenuOpen = false;
        this.isTableMenuOpen = false;
        this.isImageSizeMenuOpen = false;
        this.isCellAlignMenuOpen = false;
        this.isCellVerticalMenuOpen = false;
    }

    tiptapIsInTable(): boolean {
        return !!(this.tiptapEditor && (this.tiptapEditor as any).isActive?.('table'));
    }

    private createMentionSuggestion(): any {
        let popup: HTMLDivElement | null = null;
        let list: HTMLDivElement | null = null;
        let selectedIndex = 0;
        let currentItems: string[] = [];
        let currentCommand: ((payload: { id: string; label: string }) => void) | null = null;

        const updateList = (items: string[]) => {
            if (!list) {
                return;
            }
            list.innerHTML = '';
            items.forEach((item: string, index: number) => {
                const button = document.createElement('button');
                button.type = 'button';
                button.className = 'tiptap-mention-item';
                if (index === selectedIndex) {
                    button.classList.add('is-selected');
                }
                button.textContent = item;
                button.addEventListener('click', () => {
                    currentCommand?.({ id: item, label: item });
                });
                list?.appendChild(button);
            });
        };

        const updatePosition = (props: any) => {
            const rect = props.clientRect?.();
            if (!rect || !popup) {
                return;
            }
            popup.style.left = `${rect.left + window.scrollX}px`;
            popup.style.top = `${rect.bottom + window.scrollY + 6}px`;
        };

        return {
            char: '@',
            items: ({ query }: { query: string }) =>
                SUPPORTED_PLACE_HOLDERS.filter((item) => item.toLowerCase().includes(query.toLowerCase())),
            render: () => ({
                onStart: (props: any) => {
                    selectedIndex = 0;
                    popup = document.createElement('div');
                    popup.className = 'tiptap-mention-list';
                    list = document.createElement('div');
                    popup.appendChild(list);
                    document.body.appendChild(popup);
                    currentItems = (props.items as string[] | undefined) ?? [];
                    currentCommand = typeof props.command === 'function' ? props.command : null;
                    updateList(currentItems);
                    updatePosition(props);
                },
                onUpdate: (props: any) => {
                    selectedIndex = 0;
                    currentItems = (props.items as string[] | undefined) ?? [];
                    currentCommand = typeof props.command === 'function' ? props.command : currentCommand;
                    updateList(currentItems);
                    updatePosition(props);
                },
                onKeyDown: (props: any) => {
                    if (!list) {
                        return false;
                    }
                    if (currentItems.length === 0) {
                        return false;
                    }
                    if (props.event.key === 'ArrowDown') {
                        selectedIndex = (selectedIndex + 1) % currentItems.length;
                        updateList(currentItems);
                        return true;
                    }
                    if (props.event.key === 'ArrowUp') {
                        selectedIndex = (selectedIndex - 1 + currentItems.length) % currentItems.length;
                        updateList(currentItems);
                        return true;
                    }
                    if (props.event.key === 'Enter') {
                        const item = currentItems[selectedIndex];
                        if (item && typeof currentCommand === 'function') {
                            currentCommand({ id: item, label: item });
                            return true;
                        }
                    }
                    if (props.event.key === 'Escape') {
                        return true;
                    }
                    return false;
                },
                onExit: () => {
                    popup?.remove();
                    popup = null;
                    list = null;
                    currentItems = [];
                    currentCommand = null;
                },
            }),
        };
    }

    tiptapInsertTable(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.insertTable?.({ rows: 3, cols: 3, withHeaderRow: true });
    }

    tiptapAddColumnBefore(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.addColumnBefore?.();
    }

    tiptapAddColumnAfter(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.addColumnAfter?.();
    }

    tiptapDeleteColumn(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.deleteColumn?.();
    }

    tiptapAddRowBefore(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.addRowBefore?.();
    }

    tiptapAddRowAfter(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.addRowAfter?.();
    }

    tiptapDeleteRow(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.deleteRow?.();
    }

    tiptapDeleteTable(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.deleteTable?.();
    }

    tiptapMergeCells(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.mergeCells?.();
    }

    tiptapSplitCell(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.splitCell?.();
    }

    tiptapToggleHeaderRow(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.toggleHeaderRow?.();
    }

    tiptapToggleHeaderColumn(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.toggleHeaderColumn?.();
    }

    tiptapColumnTotal(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.setColumnStyle?.('column-Total');
    }

    tiptapColumnHighlight(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.setColumnStyle?.('column-Highlight');
    }

    tiptapColumnNumber(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.setColumnStyle?.('column-number');
    }

    tiptapClearColumnStyles(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.clearColumnStyles?.();
    }

    tiptapRowTotal(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.setRowStyle?.('row-Total');
    }

    tiptapClearRowStyles(): void {
        this.tiptapFocus();
        (this.tiptapEditor as any)?.commands?.clearRowStyles?.();
    }

    private tiptapFocus(): void {
        (this.tiptapEditor as any)?.commands?.focus?.();
    }

    public onDeleteReportClicked(): void {
        this.deleteReportClicked.emit();
    }

    public onPrintComplementaryReportClicked(): void {
        if (this.reportIndex == null) {
            return;
        }

        this.printComplementaryReport.emit({
            index: this.reportIndex,
            fileId:
                this.updatedComplementaryReports[this.reportIndex]?.fileId ||
                this.complementaryReport?.fileId,
        });
    }

    private onShowInTocToggleChanged(showInToc: boolean): void {
        this.showInToc = showInToc;
    }

    private onShowTitleInReportToggleChanged(showTitleInReport: boolean): void {
        this.showTitleInReport = showTitleInReport;
    }

    private onShowPageNumbersToggleChanged(showPageNumbers: boolean): void {
        this.showPageNumbers = showPageNumbers;
    }

    private markTitleAsDirty(): void {
        this.formGroup?.controls['title'].markAsDirty();
        this.formGroup?.controls['title'].markAsTouched();
        this.formGroup?.controls['title'].updateValueAndValidity();
    }

    public onPreviewClick(): void {
        if (!this.complementaryReport?.fileId && !this.updatedComplementaryReports[this.reportIndex]?.fileId) return;
        this.previewRequested.emit(
            this.complementaryReport?.fileId || this.updatedComplementaryReports[this.reportIndex]?.fileId,
        );
        this.isPreviewModalOpen = true;
    }
    public onClosePreviewModal(): void {
        this.isPreviewModalOpen = false;
        this.previewClosed.emit(
            this.complementaryReport?.fileId || this.updatedComplementaryReports[this.reportIndex]?.fileId,
        );
    }

    public getPreviewStatusText(status: string): string {
        switch (status) {
            case FilePreviewStatus.NOT_STARTED:
                return this.localizedTexts.t('aumico_app_cpmm_documents_wait_for_anti_virus') as string;
            case FilePreviewStatus.PENDING:
                return this.localizedTexts.t('aumico_app_cpmm_documents_preview_loading') as string;
            case FilePreviewStatus.GENERATED:
                return this.localizedTexts.t('aumico_app_cpmm_documents_preview_loading') as string;
            case FilePreviewStatus.FAILED:
                return this.localizedTexts.t('aumico_app_cpmm_documents_preview_failed') as string;
            case FilePreviewStatus.NOT_SUPPORTED:
                return this.localizedTexts.t('aumico_app_cpmm_documents_preview_not_supported') as string;
            case FilePreviewStatus.SCANNING:
                return this.localizedTexts.t('aumico_app_cpmm_documents_wait_for_anti_virus') as string;
            default:
                return status;
        }
    }
    downloadDocument() {
        // For files, use the normal download process
        this.downloadFile.emit({
            documentId: this.complementaryReport?.fileId || this.updatedComplementaryReports[this.reportIndex]?.fileId,
            documentName:
                this.complementaryReport?.fileName || this.updatedComplementaryReports[this.reportIndex]?.fileName,
        });
    }

    public openDocument(): void {
        this.openFile.emit(
            this.complementaryReport?.fileId || this.updatedComplementaryReports[this.reportIndex]?.fileId,
        );
    }
    public decodeFileName(fileName: string | undefined): string | undefined {
        const possibleMojibakeRegex = /[\u00C2-\u00F4]/;

        if (fileName == null) {
            return fileName;
        } else if (!possibleMojibakeRegex.test(fileName)) {
            return fileName;
        }
        try {
            // Fix mojibake: UTF-8 bytes were interpreted as Latin-1/ISO-8859-1
            // Re-encode as Latin-1 bytes, then decode as UTF-8
            const bytes = new Uint8Array([...fileName].map((c) => c.charCodeAt(0)));
            return new TextDecoder('utf-8').decode(bytes);
        } catch {
            // If decoding fails, return original
            return fileName;
        }
    }
}
